import {useEffect, useState} from 'react';

import {head, isNil} from 'ramda';
import {isArray, isNilOrEmpty, isNotNilOrEmpty, isNull} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {AssignedMechanic} from '@dms/api/shared';
import {usePermissions} from '@dms/shared';

import {Nullish} from 'shared/web';

interface UseBasketMechanicProps {
  authorizationProfileId: string | Nullish;
  assignedMechanicId: string | Nullish;
  competenceId?: string | Nullish;
}

export function useBasketMechanic(props: UseBasketMechanicProps) {
  const [canReadEmployee] = usePermissions({permissionKeys: ['employeeRead']});

  const [mechanicPayload, setMechanicPayload] = useState<AssignedMechanic | null>(null);

  const [mechanicError, setMechanicError] = useState<string | undefined>(undefined);

  const [isMechanicCleared, setIsMechanicCleared] = useState<boolean>(false);

  const {data: mechanics, isLoading: areMechanicsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: props.authorizationProfileId as string,
      isActive: true,
      withToOffer: true,
      employeeCompetenceId: isNotNilOrEmpty(props.competenceId)
        ? (props.competenceId as string)
        : undefined,
    },
    {
      skip: isNilOrEmpty(props.authorizationProfileId) || !canReadEmployee,
    }
  );

  useEffect(
    function syncAssignedMechanic() {
      if (isNilOrEmpty(props.assignedMechanicId)) {
        return;
      }

      const lastSelectedMechanic = mechanics?.find(
        (mechanic) => mechanic?.employeeId === props.assignedMechanicId
      );

      if (isNil(lastSelectedMechanic)) {
        // eslint-disable-next-line react/set-state-in-effect -- Pre-existing violation
        setMechanicPayload(null);
        return;
      }

      setMechanicPayload({
        id: lastSelectedMechanic.employeeId as string,
        isDefault: true,
        costCenterId: lastSelectedMechanic.costCenterId ?? null,
      });
    },
    [props.assignedMechanicId, mechanics]
  );

  const mechanicId = mechanicPayload ? mechanicPayload.id : null;

  const inputMechanicId = isMechanicCleared ? null : mechanicId;

  const handleMechanicChange = (mechanicId: string | number | string[] | null) => {
    const parsedValue = (isArray(mechanicId) ? head(mechanicId) : mechanicId) as string | null;

    setIsMechanicCleared(isNull(parsedValue));

    const selectedMechanic = mechanics?.find((mechanic) => mechanic?.employeeId === parsedValue);

    if (isNil(selectedMechanic)) {
      setMechanicPayload(null);
      return;
    }

    setMechanicPayload({
      id: selectedMechanic.employeeId as string,
      isDefault: true,
      costCenterId: selectedMechanic.costCenterId ?? null,
    });

    setMechanicError(undefined);
  };

  return {
    mechanics,
    areMechanicsLoading,
    mechanicPayload,
    inputMechanicId,
    handleMechanicChange,
    mechanicError,
    setMechanicError,
  };
}
